import { useQueryClient } from '@tanstack/react-query';
import { ImagePlus, Trash2 } from 'lucide-react';
import { useId, useRef, useState } from 'react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { api, errorMessage } from '@/lib/api';
import { type Asset, useAssetUrl } from './api';

const ACCEPT = 'image/jpeg,image/png,image/webp,image/avif';
const MAX = 8 * 1024 * 1024;

/**
 * Imagem opcional com envio para `POST /api/assets`. O texto alternativo é obrigatório e
 * o servidor converte tudo para WebP.
 */
export function ImageUploadField({
  value,
  onChange,
  purpose,
  label = 'Imagem',
  defaultAlt = '',
  shape = 'square',
  hint,
}: {
  value: string | null | undefined;
  onChange: (id: string | null) => void;
  purpose: 'logo' | 'barber' | 'service' | 'site';
  label?: string;
  defaultAlt?: string;
  shape?: 'square' | 'wide';
  hint?: string;
}) {
  const qc = useQueryClient();
  const id = useId();
  const inputRef = useRef<HTMLInputElement>(null);
  const url = useAssetUrl(value);
  const [file, setFile] = useState<File | null>(null);
  const [alt, setAlt] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string>();

  function pick(f: File | undefined) {
    setError(undefined);
    if (!f) return;
    if (!ACCEPT.split(',').includes(f.type)) return setError('Use uma imagem JPG, PNG, WebP ou AVIF.');
    if (f.size > MAX) return setError('A imagem passa de 8 MB.');
    setFile(f);
    setAlt(defaultAlt);
  }

  async function send() {
    if (!file) return;
    if (!alt.trim()) return setError('Descreva a imagem para quem não consegue vê-la.');
    setBusy(true);
    setError(undefined);
    try {
      const fd = new FormData();
      fd.append('purpose', purpose);
      fd.append('alt', alt.trim());
      fd.append('file', file);
      const a = await api.post<Asset>('/assets', fd);
      await qc.invalidateQueries({ queryKey: ['assets'] });
      onChange(a.id);
      setFile(null);
      toast.success('Imagem enviada');
    } catch (e) {
      setError(errorMessage(e));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="grid gap-2">
      <Label htmlFor={`${id}-file`}>{label}</Label>
      <div className="flex items-start gap-3">
        <div
          className={`flex shrink-0 items-center justify-center overflow-hidden rounded-md border bg-muted ${shape === 'wide' ? 'h-16 w-28' : 'size-20'}`}
        >
          {url ? <img src={url} alt="" className="size-full object-cover" /> : <ImagePlus className="size-5 text-muted-foreground" aria-hidden />}
        </div>
        <div className="grid min-w-0 flex-1 gap-2">
          <input id={`${id}-file`} ref={inputRef} type="file" accept={ACCEPT} className="sr-only" onChange={(e) => pick(e.target.files?.[0])} />
          <div className="flex flex-wrap gap-2">
            <Button type="button" variant="outline" size="sm" onClick={() => inputRef.current?.click()}>
              <ImagePlus aria-hidden /> {url ? 'Trocar imagem' : 'Escolher imagem'}
            </Button>
            {value && !file && (
              <Button type="button" variant="ghost" size="sm" onClick={() => onChange(null)}>
                <Trash2 aria-hidden /> Remover
              </Button>
            )}
          </div>
          {file && (
            <div className="grid gap-2 rounded-md border bg-muted/40 p-3">
              <p className="truncate text-xs text-muted-foreground">{file.name}</p>
              <Label htmlFor={`${id}-alt`} className="text-xs">
                Texto alternativo (obrigatório)
              </Label>
              <Input id={`${id}-alt`} value={alt} onChange={(e) => setAlt(e.target.value)} maxLength={200} placeholder="Ex.: Barbeiro finalizando um degradê" />
              <div className="flex gap-2">
                <Button type="button" size="sm" onClick={send} disabled={busy}>
                  {busy ? 'Enviando…' : 'Enviar imagem'}
                </Button>
                <Button type="button" size="sm" variant="ghost" onClick={() => setFile(null)} disabled={busy}>
                  Cancelar
                </Button>
              </div>
            </div>
          )}
          {error && (
            <p role="alert" className="text-xs text-destructive">
              {error}
            </p>
          )}
          {hint && !error && <p className="text-xs text-muted-foreground">{hint}</p>}
        </div>
      </div>
    </div>
  );
}
